import React from 'react';
import { Modal, Typography, Button } from 'antd';
import { Link } from 'react-router-dom';

import { type CDNAnalyticsExportStatus } from './CDNAnalyticsAPI';
import { usePrerenderEmail } from '../../hooks/usePrerenderUser';

function DefaultText() {
  return (
    <>
      <Typography.Paragraph>Export all crawler requests data as a CSV file.</Typography.Paragraph>
      <Typography.Paragraph>All the filters currently applied will be used for the export.</Typography.Paragraph>
    </>
  );
}

function LoadingText() {
  return (
    <>
      <Typography.Paragraph>Export in progress, please wait...</Typography.Paragraph>
    </>
  );
}

function SuccessText({ prerenderEmail }: { prerenderEmail: string }) {
  return (
    <Typography.Paragraph>
      We will email a download link to <strong>{prerenderEmail}</strong> when your export is ready. You can also access it from the <Link to="/notifications">Notifications Center</Link>.
    </Typography.Paragraph>
  );
}

function AlreadyExistsText({ prerenderEmail }: { prerenderEmail: string }) {
  return (
    <>
      <Typography.Paragraph>You already have an export in progress.</Typography.Paragraph>
      <Typography.Paragraph>
        We will email a download link to <strong>{prerenderEmail}</strong> when your export is ready. You can also access it from the <Link to="/notifications">Notifications Center</Link>.
      </Typography.Paragraph>
    </>
  );
}

function ErrorText() {
  return (
    <Typography.Paragraph>An error occurred while exporting your data. Please try again later.</Typography.Paragraph>
  );
}

type CDNAnalyticsExportModalProps = {
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onOk: () => void;
  isError: boolean;
  status?: CDNAnalyticsExportStatus;
};

function CDNAnalyticsExportModal({ open, onClose, onOk, isLoading, isError, status }: CDNAnalyticsExportModalProps) {
  const prerenderEmail = usePrerenderEmail();
  const modalTitle = status === 'queued' ? 'Crawler Requests export scheduled' : 'Export Crawler Requests';
  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={onClose}
      footer={
        isError || !!status
          ? [
              <Button key="close" onClick={onClose} type="primary">
                Close
              </Button>,
            ]
          : [
              <Button key="close" onClick={onClose}>
                Close
              </Button>,
              <Button key="export" type="primary" onClick={onOk} loading={isLoading}>
                Export
              </Button>,
            ]
      }
    >
      {isLoading && <LoadingText />}
      {(isError || status === 'not queued') && <ErrorText />}
      {status === 'already exists' && <AlreadyExistsText prerenderEmail={prerenderEmail} />}
      {status === 'queued' && <SuccessText prerenderEmail={prerenderEmail} />}
      {!isLoading && !isError && !status && <DefaultText />}
    </Modal>
  );
}

export default CDNAnalyticsExportModal;
